import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaLock, FaCheckCircle, FaCheck, FaCircle } from "react-icons/fa";
import { resetPassword, verifyResetToken } from "../api/password-reset";
import {
  validatePassword,
  validatePasswordMatch,
  validateResetCode,
} from "../utils/passwordValidation";
import { Button, Field, Input, PageSpinner, PasswordInput } from "../ui";
import AuthLayout, { AuthLink } from "../components/site/AuthLayout";

const Requirement = ({ ok, children }) => (
  <li className={`flex items-center gap-2 ${ok ? "text-success" : "text-white/60"}`}>
    {ok ? <FaCheck className="text-xs" /> : <FaCircle className="text-[6px] mx-[3px]" />}
    {children}
  </li>
);

const PasswordReset = () => {
  const [searchParams] = useSearchParams();

  // Get token and email from URL parameters
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  // Form state
  // Typed when the link carried no email (code method).
  const [emailInput, setEmailInput] = useState("");
  const resetEmail = email || emailInput.trim().toLowerCase();
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [useCode, setUseCode] = useState(false);

  const verifyToken = useCallback(async () => {
    setVerifying(true);
    try {
      const response = await verifyResetToken(token, email);

      if (response.success) {
        setTokenValid(true);
        setUserInfo(response.user);
      } else {
        toast.error(response.message || "الرابط غير صالح أو منتهي الصلاحية");
        // Fallback to code input
        setUseCode(true);
      }
    } catch (error) {
      toast.error("الرابط غير صالح أو منتهي الصلاحية");
      setUseCode(true);
    } finally {
      setVerifying(false);
    }
  }, [token, email]);

  // Verify token on component mount
  useEffect(() => {
    if (token && email) {
      verifyToken();
    } else {
      // If no token/email in URL, show reset code input
      setUseCode(true);
    }
  }, [token, email, verifyToken]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    // Validate email if using code
    if (useCode && !resetEmail) {
      toast.error("البريد الإلكتروني مطلوب");
      return;
    }

    // Validate reset code if using code method
    if (useCode) {
      const codeValidation = validateResetCode(resetCode);
      if (!codeValidation.isValid) {
        toast.error(codeValidation.error);
        return;
      }
    }

    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      passwordValidation.errors.forEach((error) => toast.error(error));
      return;
    }

    // Validate password match
    const matchValidation = validatePasswordMatch(newPassword, confirmPassword);
    if (!matchValidation.isValid) {
      toast.error(matchValidation.error);
      return;
    }

    setLoading(true);
    try {
      const response = await resetPassword(
        useCode ? null : token,
        useCode ? resetCode : null,
        resetEmail,
        newPassword
      );

      if (response.success) {
        setResetComplete(true);
        toast.success("تم إعادة تعيين كلمة المرور بنجاح!");
      } else {
        toast.error(
          response.message || "حدث خطأ أثناء إعادة تعيين كلمة المرور"
        );
      }
    } catch (error) {
      toast.error(error.message || "حدث خطأ أثناء إعادة تعيين كلمة المرور");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while verifying token
  if (verifying) {
    return (
      <AuthLayout title="إعادة تعيين كلمة المرور">
        <PageSpinner label="جاري التحقق من الرابط..." />
      </AuthLayout>
    );
  }

  // Show success state after password reset
  if (resetComplete) {
    return (
      <AuthLayout title="تم تغيير كلمة المرور">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-5">
            <FaCheckCircle className="text-2xl" />
          </div>
          <p className="text-white/80 leading-relaxed">
            تم تحديث كلمة المرور. تقدر الحين تسجّل الدخول بكلمة المرور الجديدة.
          </p>
        </div>
        <div className="mt-6 space-y-3">
          <Button block size="lg" to="/login">
            تسجيل الدخول
          </Button>
          <Button block variant="ghost" to="/">
            العودة إلى الصفحة الرئيسية
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const hasLength = newPassword.length >= 8;
  const hasLetter = /(?=.*[a-zA-Z])/.test(newPassword);
  const hasDigit = /(?=.*\d)/.test(newPassword);

  return (
    <AuthLayout
      title="كلمة مرور جديدة"
      subtitle={
        userInfo
          ? `مرحباً، ${userInfo.name}`
          : !tokenValid && useCode
          ? "اكتب الكود المرسل إلى بريدك وكلمة المرور الجديدة"
          : undefined
      }
      footer={
        <>
          ما وصلك الكود؟{" "}
          <AuthLink to="/password-reset-request">اطلب كوداً جديداً</AuthLink>
        </>
      }
    >
      <form onSubmit={handlePasswordReset} className="space-y-5" noValidate>
        {useCode && !email && (
          <Field label="البريد الإلكتروني" htmlFor="email">
            <Input
              ltr
              type="email"
              id="email"
              autoComplete="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="example@email.com"
              required
              disabled={loading}
            />
          </Field>
        )}

        {useCode && (
          <Field label="كود إعادة التعيين (6 أرقام)" htmlFor="resetCode">
            <Input
              ltr
              size="lg"
              id="resetCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={resetCode}
              onChange={(e) =>
                setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              className="text-center font-mono tracking-[0.4em]"
              placeholder="123456"
              maxLength={6}
              required
              disabled={loading}
            />
          </Field>
        )}

        <Field label="كلمة المرور الجديدة" htmlFor="newPassword">
          <PasswordInput
            id="newPassword"
            autoComplete="new-password"
            shown={showPassword}
            onToggle={() => setShowPassword((v) => !v)}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="كلمة المرور الجديدة"
            required
            disabled={loading}
          />
        </Field>

        <Field label="تأكيد كلمة المرور" htmlFor="confirmPassword">
          <PasswordInput
            id="confirmPassword"
            autoComplete="new-password"
            shown={showPassword}
            onToggle={() => setShowPassword((v) => !v)}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="أعد كتابة كلمة المرور"
            required
            disabled={loading}
          />
        </Field>

        <div className="rounded-xl bg-white/[0.05] border border-white/10 p-4">
          <p className="text-sm font-bold mb-2">متطلبات كلمة المرور</p>
          <ul className="text-sm space-y-1">
            <Requirement ok={hasLength}>8 أحرف على الأقل</Requirement>
            <Requirement ok={hasLetter}>حرف واحد على الأقل</Requirement>
            <Requirement ok={hasDigit}>رقم واحد على الأقل</Requirement>
          </ul>
        </div>

        <Button
          type="submit"
          size="lg"
          block
          loading={loading}
          disabled={!newPassword || !confirmPassword || (useCode && !resetCode)}
          icon={<FaLock />}
        >
          تعيين كلمة المرور
        </Button>

        <p className="text-xs text-white/55 text-center leading-relaxed">
          بعد التغيير تحتاج تسجّل الدخول من جديد على كل أجهزتك.
        </p>
      </form>
    </AuthLayout>
  );
};

export default PasswordReset;
